import React, { useState, useEffect } from "react";
import { Steps, Button, Card, Spin, Tooltip, Alert, message } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faBuilding,
  faServer,
  faGears,
  faEthernet,
  faCodeBranch,
  faBuildingShield,
  faElevator,
  faStopwatch,
  faNetworkWired,
  faUsers,
  faEnvelopeCircleCheck,
  faUpDown,
  faTable,
  faFileCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
// CONTEXT
import { WizardProvider, useWizard, type WizardState } from "./WizardContext";
import {
  computeStepVisual,
  STEP_VISUAL_COLOR,
  stepSatisfiedForNavigation,
} from "./utils/wizardStepStatus";
import { firstGapBefore } from "./utils/wizardCompletion";
// DRAWER
import WizardDetailDrawer from "./WizardDetailDrawer";
// STEPS
import StepOrganization from "./steps/StepOrganization";
import StepServer from "./steps/StepServer";
import StepAdapters from "./steps/StepAdapters";
import StepChannels from "./steps/StepChannels";
import StepChannelModifiers from "./steps/StepChannelModifiers";
import StepBanks from "./steps/StepBanks";
import StepLiftsFloors from "./steps/StepLiftsFloors";
import StepDebounce from "./steps/StepDebounce";
import StepBacnet from "./steps/StepBacnet";
import StepUsersRoles from "./steps/StepUsersRoles";
import StepNotifications from "./steps/StepNotifications";
import StepActions from "./steps/StepActions";
import StepMonitorGrid from "./steps/StepMonitorGrid";
import StepReview from "./steps/StepReview";
import WizardExportButton from "./components/WizardExportButton";
// STORE
import { RootState } from "../../app/store";
// WIZARD STEP LIST
import { WIZARD_STEPS, type StepKey } from "./wizardSteps";
import { WizardStepGate } from "./WizardStepGate";
import { useScreenPermission } from "../../customHooks/useScopedPermission";
import UnauthorizedPage from "../unauthorized/UnauthorizedPage";

// The backend wizexport.Stage each step saves through, or `null` when it saves
// nothing — the top-level export button is disabled on those steps. Debounce
// has no dedicated stage; its configuration rides along with the Adapters keys
// export.
//
// Keyed by step rather than positional: the stage numbers are the backend's,
// not the wizard's, and the two sequences differ precisely because Debounce is
// absent from the backend's. A positional array made that an off-by-one waiting
// to happen every time a step was inserted, which is exactly what happened when
// Channel Modifiers landed.
//
// Source of truth for the values: LiftCloud/utils/wizexport/types.go (Stage iota).
const STEP_TO_BACKEND_STAGE: Record<StepKey, number | null> = {
  Organization: 0,
  Server: 1,
  Adapters: 2,
  Channels: 3,
  Banks: 4,
  ChannelModifiers: 5,
  Lifts: 6,
  Debounce: null, // rides along with the Adapters keys export
  Bacnet: 7,
  Users: 8,
  Notifications: 9,
  Actions: 10,
  MonitorGrid: 11,
  Review: 12,
};

// Steps are lazy-mounted (first visit) and kept alive via CSS display:none so
// that local state (useState) survives navigation between steps.
const STEP_COMPONENTS: Record<StepKey, React.ElementType> = {
  Organization: StepOrganization,
  Server: StepServer,
  Adapters: StepAdapters,
  Channels: StepChannels,
  Banks: StepBanks,
  ChannelModifiers: StepChannelModifiers,
  Lifts: StepLiftsFloors,
  Debounce: StepDebounce,
  Bacnet: StepBacnet,
  Users: StepUsersRoles,
  Notifications: StepNotifications,
  Actions: StepActions,
  MonitorGrid: StepMonitorGrid,
  Review: StepReview,
};

// "Has this step got any data yet?" — one probe per step, read by
// computeStepVisual to tell a genuinely-skipped optional step from a completed
// one.
//
// Keyed rather than a switch on the index. The switch this replaces ended in
// `default: return false`, so a step added to the wizard and not added here
// answered "no data" forever, silently and plausibly — a step that renders as
// skipped when it is full. A Record has no default: the step is a compile
// error until someone says what having data means for it.
const STEP_HAS_DATA: Record<StepKey, (state: WizardState) => boolean> = {
  Organization: (state) => !!state.organizationId,
  Server: (state) => !!state.serverId,
  Adapters: (state) => state.adapterIds.length > 0,
  Channels: (state) => state.channelIds.length > 0,
  Banks: (state) => state.bankIds.length > 0,
  ChannelModifiers: (state) => state.hasChannelModifiers,
  Lifts: (state) => Object.keys(state.liftIdsByBank).length > 0,
  Debounce: (state) => state.hasDebounceOverrides,
  Bacnet: (state) => state.hasBacnetConfig,
  Users: (state) => state.roleIds.length > 0 || state.userIds.length > 0,
  Notifications: (state) => state.notificationIds.length > 0,
  Actions: (state) => state.actionCatalogIds.length > 0,
  MonitorGrid: (state) => !!state.monitorGridId,
  Review: () => true,
};

// Header icon per step. Lives here rather than in wizardSteps.ts so that module
// stays import-free — see its header for why that matters.
const STEP_ICONS: Record<StepKey, IconDefinition> = {
  Organization: faBuilding,
  Server: faServer,
  Adapters: faGears,
  Channels: faEthernet,
  Banks: faBuildingShield,
  ChannelModifiers: faCodeBranch,
  Lifts: faElevator,
  Debounce: faStopwatch,
  Bacnet: faNetworkWired,
  Users: faUsers,
  Notifications: faEnvelopeCircleCheck,
  Actions: faUpDown,
  MonitorGrid: faTable,
  Review: faFileCircleCheck,
};

function WizardContent() {
  const { theme } = useSelector((state: RootState) => state.home);
  const navigate = useNavigate();
  const {
    state,
    goToStep,
    isStepCompleted,
    isHydrating,
    hydrationError,
    stepValidationMessage,
    staleServerError,
    clearStaleServerErrorAndReroute,
  } = useWizard();
  const { currentStep } = state;

  // Track which steps have been visited so we only mount them once
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([0]));

  useEffect(() => {
    setVisitedSteps((prev) => {
      if (prev.has(currentStep)) return prev;
      const next = new Set(prev);
      next.add(currentStep);
      return next;
    });
  }, [currentStep]);

  // computeStepVisual asks by index; the table is keyed, so translate through
  // the ordered list. An index outside the wizard has no step and no data.
  const hasData = (index: number): boolean => {
    const step = WIZARD_STEPS[index];
    return step ? STEP_HAS_DATA[step.key](state) : false;
  };

  const getStepColor = (index: number): string => {
    const visual = computeStepVisual(index, {
      completedSteps: state.completedSteps,
      currentStep,
      hasData,
    });
    return STEP_VISUAL_COLOR[visual];
  };

  const getStepStatus = (index: number): "finish" | "process" | "wait" => {
    if (isStepCompleted(index)) return "finish";
    if (index === currentStep) return "process";
    return "wait";
  };

  const canGoNext = () => isStepCompleted(currentStep) && currentStep < WIZARD_STEPS.length - 1;

  const canGoPrevious = () => currentStep > 0;

  const handleStepClick = (step: number) => {
    // Allow unconditional backward/same-step navigation, and a forward jump
    // only when every step before the target is satisfied.
    //
    // "Satisfied" means either genuinely complete, or a wizardStepStatus
    // OPTIONAL_STEPS member (Channel Modifiers, Debounce, BACnet,
    // Notifications, Actions, Monitor Grid) — those auto-complete the moment
    // the operator visits them and are only ever a dismissible warning on
    // Review, never a hard publish gate (see StepReview's canPublish), so
    // requiring them complete here would add navigation friction with no
    // correctness benefit. computeStepVisual applies this exact same
    // OPTIONAL_STEPS set at its own call site; filterEmptyOptionalSteps
    // applies a conceptually similar but separately-hardcoded, narrower
    // exemption (only Notifications/Actions/MonitorGrid, not the full set) —
    // don't assume the two are interchangeable. StepReview.tsx also has its
    // own differently-membered local OPTIONAL_STEPS for its publish-warning
    // list; none of these three lists agree with each other today (a
    // pre-existing gap, not introduced here — e.g. Debounce is exempted from
    // navigation friction by this guard but never even mentioned by Review's
    // publish warning).
    //
    // The exemption is deliberately unconditional rather than checking
    // whether hydration/re-visiting already cleared each optional step: today
    // only Channel Modifiers is ever genuinely gapped this way (the other
    // five auto-cascade on hydration too), so the exemption is broader than
    // the one real case it exists for. Accepted for now as the simpler rule;
    // reconsider if a future REQUIRED step is ever added that depends on one
    // of these optional steps' actual content, since this exemption would let
    // an operator jump past it unvisited.
    //
    // Previously this only checked the target and the immediately-prior step
    // (`isStepCompleted(step) || isStepCompleted(step - 1)`), a two-bit sample
    // of what should be a full walk. That let a stale-complete later step
    // become a one-click hop-in target that silently skipped every genuinely
    // incomplete required step before it — the smart per-field UPDATE
    // invalidation rules never route any field to MonitorGrid or Review, so
    // an edit could leave, say, Banks unmarked while Review stayed
    // stale-complete. A full prefix scan closes that regardless of which
    // step the gap is at.
    if (step > currentStep) {
      const gap = firstGapBefore(step, stepSatisfiedForNavigation(isStepCompleted));
      if (gap !== null) {
        const blockerTitle = WIZARD_STEPS[gap]?.title;
        const targetTitle = WIZARD_STEPS[step]?.title ?? "that step";
        message.info(
          blockerTitle
            ? `Finish ${blockerTitle} before moving to ${targetTitle}.`
            : "Finish the earlier steps first.",
        );
        return;
      }
    }

    // Mark the target visited in the same update as the navigation. The
    // content div is gated on visitedSteps.has(index); relying on the
    // [currentStep] effect to add it leaves the target blank for one render
    // when jumping to a not-yet-visited step (looked like a needed 2nd click).
    setVisitedSteps((prev) => {
      if (prev.has(step)) return prev;
      const next = new Set(prev);
      next.add(step);
      return next;
    });
    goToStep(step);
  };

  if (isHydrating) {
    return (
      <div className={`layout__contentPage__${theme}`}>
        <Card style={{ margin: "16px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <Spin tip="Loading wizard data..." size="large">
              <div style={{ padding: 50 }} />
            </Spin>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={`layout__contentPage__${theme}`}>
      <Card style={{ margin: "16px" }}>
        {hydrationError && (
          <Alert
            type="warning"
            showIcon
            message="Wizard data partially loaded"
            description={hydrationError}
            style={{ marginBottom: 16 }}
          />
        )}
        {staleServerError && (
          <Alert
            type="error"
            showIcon
            closable={false}
            data-testid="wizard-stale-server-banner"
            message="The server this wizard was configuring has been deleted"
            description={
              <div>
                <p style={{ margin: "0 0 8px 0" }}>
                  Every save and create attempt is failing with{" "}
                  <code style={{ background: "#0008", padding: "1px 4px", borderRadius: 3 }}>
                    {staleServerError.message}
                  </code>{" "}
                  because the server (id <code>{staleServerError.serverId || "(unset)"}</code>) no
                  longer exists on the backend &mdash; most commonly because an edge-to-cloud sync
                  marked its <code>deleted_at</code> field without cascading to the channels and
                  adapters this wizard depends on.
                </p>
                <p style={{ margin: "0 0 12px 0" }}>
                  To continue, go back to the Server step and pick a different existing server, or
                  create a new one. The channels, banks, and adapters that referenced the deleted
                  server will need to be reconfigured against the new one.
                </p>
                <Button
                  data-testid="wizard-stale-server-reroute"
                  type="primary"
                  danger
                  onClick={clearStaleServerErrorAndReroute}
                >
                  Go to Server step
                </Button>
              </div>
            }
            style={{ marginBottom: 16 }}
          />
        )}
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <h2 style={{ margin: 0 }}>
              {(() => {
                if (state.orgStatus === "ACTIVE") return "Edit Organization";
                if (state.organizationId) return "Resume Organization Setup";
                return "Setup New Organization";
              })()}
            </h2>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <WizardExportButton
                stage={STEP_TO_BACKEND_STAGE[WIZARD_STEPS[currentStep].key] ?? 0}
                orgId={state.organizationId}
                disabled={STEP_TO_BACKEND_STAGE[WIZARD_STEPS[currentStep].key] === null}
              />
              <Button
                onClick={() =>
                  navigate(
                    state.orgStatus === "ACTIVE"
                      ? "/organization-management/organizations"
                      : "/organization-management/drafts",
                  )
                }
              >
                Exit Wizard
              </Button>
            </div>
          </div>
          <Steps
            current={currentStep}
            size="small"
            onChange={handleStepClick}
            items={WIZARD_STEPS.map((step, index) => ({
              title: step.title,
              icon: (
                <FontAwesomeIcon
                  icon={STEP_ICONS[step.key]}
                  style={{ color: getStepColor(index) }}
                />
              ),
              status: getStepStatus(index),
            }))}
          />
        </div>

        {/* STEP CONTENT — lazy-mount + keep-alive */}
        {WIZARD_STEPS.map((step, index) => {
          if (!visitedSteps.has(index)) return null;
          const Component = STEP_COMPONENTS[step.key];
          return (
            <div key={step.key} style={{ display: index === currentStep ? "block" : "none" }}>
              <WizardStepGate step={step.key}>
                <Component />
              </WizardStepGate>
            </div>
          );
        })}

        {/* NAVIGATION */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 24,
            paddingTop: 16,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Button
            disabled={!canGoPrevious()}
            onClick={() => goToStep(currentStep - 1)}
            size="large"
            style={{ width: "150px" }}
          >
            <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: "10px" }} />
            Previous
          </Button>
          <Tooltip
            title={!canGoNext() && stepValidationMessage ? stepValidationMessage : ""}
            placement="topRight"
          >
            <span>
              <Button
                type="primary"
                disabled={!canGoNext()}
                onClick={() => goToStep(currentStep + 1)}
                size="large"
                style={{ width: "150px" }}
              >
                Next
                <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: "10px" }} />
              </Button>
            </span>
          </Tooltip>
        </div>
      </Card>
    </div>
  );
}

export default function SetupWizardPage() {
  // Reads SCREEN_PERMISSION["setup-wizard"]. Checked before the provider so a
  // user without Wizard access never loads the organization into it.
  const allowed = useScreenPermission("setup-wizard");
  const [searchParams] = useSearchParams();
  const resumeOrgId = searchParams.get("orgId");
  const stepParam = searchParams.get("step");
  const initialStep = stepParam !== null ? parseInt(stepParam, 10) : undefined;

  if (!allowed) return <UnauthorizedPage />;

  return (
    <WizardProvider resumeOrgId={resumeOrgId} initialStep={initialStep}>
      <WizardContent />
      <WizardDetailDrawer />
    </WizardProvider>
  );
}
